import cutoffData from '../data/ugeac/UGEAC_Engineering_Cutoffs_2026.json';
import { getCollegePriority } from './collegePriority.js';
import { collegeWebsiteMap, getCanonicalCollegeName } from './seatMatrix.js';

const DEFAULT_PLACEMENT = [
  'Core, IT support, and service roles are the most common paths students target from this college.',
  'Students usually improve outcomes through internships, coding practice, projects, and off-campus preparation.',
];

const DEFAULT_NOTES = [
  'Review branch strength, hostel comfort, faculty support, and travel distance before final choice filling.',
  'Lower-cutoff colleges can still work well when the branch, effort level, and peer group match your goals.',
];

const DEFAULT_FACILITIES = ['Hostel', 'Labs', 'Library', 'Sports', 'Internet/WiFi'];

const DEFAULT_HOSTEL_MESS_FEES = {
  academicFees: {
    admissionFee: 'Rs. 3,000 per year',
    examFee: 'Rs. 3,710 per semester',
    miscellaneousFee: 'Rs. 2,000 - Rs. 5,000 per year',
  },
  hostelMessFees: {
    hostelFee: 'Rs. 6,500 - Rs. 7,500 per semester',
    messFee: 'Rs. 19,000 - Rs. 20,000 per semester',
  },
  yearlyBreakdown: {
    academicFee: 'Rs. 10,420 - Rs. 14,420',
    hostelMessFee: 'Rs. 51,000',
    totalFee: 'Rs. 61,420 - Rs. 65,420',
  },
  firstYearExtras: {
    registrationFee: 'Rs. 3,000 one-time',
  },
  fourYearTotal: 'Rs. 2.48 lakh - Rs. 2.65 lakh',
  note: 'Fees may vary slightly every year. Always verify the final amount from the college before admission.',
};

const COLLEGE_METADATA = {
  'Govt. Engg. College, Jamui': {
    shortName: 'GEC Jamui',
    city: 'Jamui',
    affiliation: 'Bihar Engineering University, Patna',
    overview:
      'Government Engineering College Jamui is one of the newer Bihar government colleges and has become a practical option for students looking for affordable government engineering education with growing demand.',
    placementInsights: [
      'ECE students often balance both core electronics preparation and software placement preparation.',
      'Students who stay active with internships, communication skills, and project work generally create better off-campus opportunities.',
    ],
    notes: [
      'This college matters personally to the creator because he is an ECE 2023 alumnus from GEC Jamui.',
      'For students considering Jamui, campus discipline, peer network, and self-driven preparation make a major difference in overall experience.',
    ],
    locationAdvantage: {
      nearbyCity: 'Jamui district town with regular road access to nearby Bihar hubs',
      connectivity: 'Connected by road and regional rail movement through Jamui side routes',
      environment: 'Developing district setting with a quieter academic atmosphere',
      railwayStation: 'Jamui Railway Station',
      stationDistance: '~8-10 km',
      travelTime: '20-30 minutes',
      stationRoute:
        'Auto and local transport options are usually available from the station side toward the college route.',
      stationFare: 'Approx. fare varies by vehicle and sharing option',
    },
  },
  'M.I.T. Muzaffarpur': {
    shortName: 'MIT Muzaffarpur',
    city: 'Muzaffarpur',
    affiliation: 'Bihar Engineering University, Patna',
    overview:
      'Muzaffarpur Institute of Technology is one of the oldest and most recognized engineering colleges in Bihar, and it usually stays high on preference lists for students seeking established brand value.',
  },
  'B.C.E. Bhagalpur': {
    shortName: 'BCE Bhagalpur',
    city: 'Bhagalpur',
    affiliation: 'Bihar Engineering University, Patna',
    overview:
      'Bhagalpur College of Engineering is among the older, better-known Bihar colleges and usually receives strong preference from students who want a traditional government engineering campus.',
  },
  'Nalanda College. Of Engg,Chandi': {
    shortName: 'NCE Chandi',
    city: 'Chandi',
    affiliation: 'Bihar Engineering University, Patna',
    overview:
      'Nalanda College of Engineering, Chandi is widely seen as one of the stronger established government colleges after the oldest flagship campuses.',
  },
  'B.C.E. Bakhtiyarpur': {
    shortName: 'BCE Bakhtiyarpur',
    city: 'Bakhtiyarpur',
    affiliation: 'Bihar Engineering University, Patna',
    overview:
      'Bakhtiyarpur College of Engineering is a recognized option for students who want a known government college with decent branch availability and strong admission demand.',
    locationAdvantage: {
      nearbyCity: 'Bakhtiyarpur with easy access toward Patna side',
      connectivity: 'Good road and rail connectivity compared with many interior campuses',
      environment: 'Semi-urban setting with practical travel convenience for students',
      railwayStation: 'Bakhtiyarpur Junction',
      stationDistance: '~3-5 km',
      travelTime: '10-15 minutes',
      stationRoute:
        'Auto and e-rickshaw options are usually available outside the station for direct college access.',
      stationFare: 'Approx. fare: Rs. 20-Rs. 100 depending on vehicle and sharing',
    },
  },
  'G.C.E. Gaya': {
    shortName: 'GCE Gaya',
  },
  'D.C.E. Darbhanga': {
    shortName: 'DCE Darbhanga',
  },
  'M..C.E. Motihari': {
    shortName: 'MCE Motihari',
  },
  'L.N.J.P.I.T. Technology. Chapra': {
    shortName: 'LNJPIT Chapra',
  },
  'S.I.T. Sitamarhi': {
    shortName: 'SIT Sitamarhi',
  },
  'K.C.E., Katihar': {
    shortName: 'KEC Katihar',
  },
  'Purnea College Of Engg.': {
    shortName: 'PCE Purnea',
  },
  'Saharsa College Of Engg.': {
    shortName: 'SCE Saharsa',
  },
  'Supaul Engg. College, Supaul': {
    shortName: 'SCE Supaul',
  },
  'B.P.M.C.E. Madhepura': {
    shortName: 'BPMCE Madhepura',
  },
  'R.R.S.D.C.E, Begusarai': {
    shortName: 'Begusarai Engineering College',
  },
  'Shri Phanishwar Nath Renu Engg. College, Araria': {
    shortName: 'GEC Araria',
  },
  'Govt. Engg. College, Arwal': {
    shortName: 'GEC Arwal',
  },
  'Govt. Engg. College, Aurangabad': {
    shortName: 'GEC Aurangabad',
  },
  'Govt. Engg. College, Banka': {
    shortName: 'GEC Banka',
  },
  'Govt. Engg. College, Buxar': {
    shortName: 'GEC Buxar',
  },
  'Govt. Engg. College, Gopalganj': {
    shortName: 'GEC Gopalganj',
  },
  'Govt. Engg. College, Jehanabad': {
    shortName: 'GEC Jehanabad',
  },
  'Govt. Engg. College, Kaimur': {
    shortName: 'GEC Kaimur',
  },
  'Govt. Engg. College, Khagaria': {
    shortName: 'GEC Khagaria',
  },
  'Govt.Engg. College Kishanganj': {
    shortName: 'GEC Kishanganj',
  },
  'Govt. Engg. College Of Lakhisarai': {
    shortName: 'GEC Lakhisarai',
  },
  'Govt. Engg. College, Madhubani': {
    shortName: 'GEC Madhubani',
  },
  'Govt. Engg.College Munger': {
    shortName: 'GEC Munger',
  },
  'Govt. Engg. College, Nawada': {
    shortName: 'GEC Nawada',
  },
  'Govt. Engg. College, Sheikhpura': {
    shortName: 'GEC Sheikhpura',
  },
  'Govt Engg. College Sheohar': {
    shortName: 'GEC Sheohar',
  },
  'Govt. Engg. College, Siwan': {
    shortName: 'GEC Siwan',
  },
  'Govt. Engg. College, Vaishali': {
    shortName: 'GEC Vaishali',
  },
  'Govt Engg College W. Champaran': {
    shortName: 'GEC West Champaran',
  },
};

const deriveCityFromCollegeName = (name) => {
  if (!name) return 'Bihar';

  const normalized = name.replace(/\s+/g, ' ').trim();
  if (normalized.includes(',')) {
    const lastPart = normalized.split(',').pop()?.trim();
    if (lastPart) {
      return lastPart.replace(/\bBihar\b/gi, '').replace(/\./g, '').trim() || 'Bihar';
    }
  }

  const tokens = normalized.split(' ');
  return tokens[tokens.length - 1].replace(/\./g, '') || 'Bihar';
};

const buildLocationAdvantage = (name, metadata = {}) => {
  if (metadata.locationAdvantage) {
    return metadata.locationAdvantage;
  }

  const city = metadata.city || deriveCityFromCollegeName(name);

  return {
    nearbyCity: city,
    connectivity: `Road connectivity around ${city} is usually the main access mode, with local rail support depending on the district.`,
    environment: 'Mixed academic and district-town environment, usually calmer than a large metro campus.',
    railwayStation: `${city} Railway Station`,
    stationDistance: '~5-12 km',
    travelTime: '15-30 minutes',
    stationRoute:
      'Auto, e-rickshaw, or local transport is generally the easiest way to reach the campus from the station.',
    stationFare: 'Approx. fare depends on distance and shared vs reserved ride',
  };
};

const buildGoogleMapsLink = (name, city) => {
  const query = encodeURIComponent(`${name}, ${city}, Bihar`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

export const getCollegeSlug = (college) =>
  college
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const buildCollegeDirectory = () => {
  const grouped = cutoffData.reduce((accumulator, row) => {
    if (!accumulator[row.college]) {
      const metadata = COLLEGE_METADATA[row.college] || {};
      const canonicalCollegeName = getCanonicalCollegeName(row.college);
      accumulator[row.college] = {
        name: row.college,
        slug: getCollegeSlug(row.college),
        shortName: metadata.shortName || row.college,
        ownership: row.ownership || 'Government',
        city: metadata.city || deriveCityFromCollegeName(row.college),
        affiliation: metadata.affiliation || null,
        establishedYear: metadata.establishedYear || null,
        overview:
          metadata.overview ||
          `${row.college} is part of the Bihar engineering admission ecosystem and appears in the imported UGEAC cutoff dataset.`,
        branches: new Set(),
        cutoffs: [],
        categories: new Set(),
        seatTypes: new Set(),
        placementInsights: metadata.placementInsights || DEFAULT_PLACEMENT,
        notes: metadata.notes || DEFAULT_NOTES,
        facilities: metadata.facilities || DEFAULT_FACILITIES,
        locationAdvantage: buildLocationAdvantage(row.college, metadata),
        googleMapsLink:
          metadata.googleMapsLink ||
          buildGoogleMapsLink(
            row.college,
            metadata.city || deriveCityFromCollegeName(row.college),
          ),
        officialWebsite:
          metadata.officialWebsite ||
          collegeWebsiteMap[row.college] ||
          collegeWebsiteMap[canonicalCollegeName] ||
          null,
        hostelMessFees: metadata.hostelMessFees || DEFAULT_HOSTEL_MESS_FEES,
      };
    }

    accumulator[row.college].branches.add(row.branch);
    accumulator[row.college].categories.add(row.category);
    accumulator[row.college].seatTypes.add(row.seatType);
    accumulator[row.college].cutoffs.push(row);
    return accumulator;
  }, {});

  return Object.values(grouped)
    .map((college) => {
      const sortedCutoffs = [...college.cutoffs].sort((first, second) => first.closingRank - second.closingRank);
      const bestCutoff = sortedCutoffs[0];
      const toughestCutoff = sortedCutoffs[sortedCutoffs.length - 1];

      return {
        ...college,
        branches: [...college.branches].sort(),
        categories: [...college.categories].sort(),
        seatTypes: [...college.seatTypes].sort(),
        priority: getCollegePriority(college.name),
        bestCutoff,
        toughestCutoff,
        cutoffRange: {
          min: bestCutoff?.closingRank || null,
          max: toughestCutoff?.closingRank || null,
        },
      };
    })
    .sort((first, second) => first.priority - second.priority || first.name.localeCompare(second.name));
};

export const collegeDirectory = buildCollegeDirectory();

export const getCollegeBySlug = (slug) =>
  collegeDirectory.find((college) => college.slug === slug);

export const getFeaturedColleges = () => collegeDirectory;
